import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { Wallet, Shield, CheckCircle } from 'lucide-react';

interface AuthPageProps {
  setUser: (user: any) => void;
}

const AuthPage: React.FC<AuthPageProps> = ({ setUser }) => {
  const navigate = useNavigate();
  const { connect, loading } = useWallet();
  const [isConnecting, setIsConnecting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      await connect();
      setUser({
        address: '0x1234...5678',
        balance: '1,250.5 SUI',
        membership: 'VIP'
      });
      setToastMessage('Wallet connected successfully! Redirecting to dashboard...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Connection failed:', error);
      setToastMessage('Connection failed. Please try again.');
      setTimeout(() => setToastMessage(''), 3000);
    } finally {
      setIsConnecting(false);
    }
  };

  const features = [
    { icon: '🔐', title: 'Secure Wallet Connection', description: 'Connect with your preferred Sui wallet' },
    { icon: '🏇', title: 'Access to Syndicate', description: 'Join the exclusive racing syndicate' },
    { icon: '💰', title: 'Earn Rewards', description: 'Participate in governance and earn yields' },
  ];

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 bg-slate-800 border border-slate-700 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-slideIn">
          {toastMessage}
        </div>
      )}
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4">🏇</div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Welcome to <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">SUI-ndicate</span>
          </h1>
          <p className="text-xl text-slate-400">
            Connect your wallet to join the world's first blockchain-based thoroughbred racing syndicate
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Features */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white mb-6">Why Join SUI-ndicate?</h2>
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="text-3xl">{feature.icon}</div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Connection Card */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
            <div className="text-center mb-6">
              <Wallet className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Connect Your Wallet</h2>
              <p className="text-slate-400">Choose your preferred wallet to get started</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleConnect}
                disabled={loading || isConnecting}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-4 px-6 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {loading || isConnecting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <Wallet className="w-5 h-5" />
                    <span>Connect Wallet</span>
                  </>
                )}
              </button>

              <div className="text-center">
                <p className="text-slate-400 text-sm">
                  By connecting, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </div>

            {/* Security Features */}
            <div className="mt-8 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Security Features</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span className="text-slate-400 text-sm">Multi-signature treasury protection</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-slate-400 text-sm">Audited smart contracts</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span className="text-slate-400 text-sm">Insurance coverage included</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
